import useMediaQuery from '@mui/material/useMediaQuery';
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { AppTheme } from 'src/themes/muiTheme';

type MobilePaymentPageBodyPart = 'add-parties' | 'checkout';

interface PaymentLayoutContext {
  isMobile: boolean;
  mobileBodyPart: MobilePaymentPageBodyPart;
  updateMobileBodyPart: (part: MobilePaymentPageBodyPart) => void;
}

const paymentLayoutContext = createContext<PaymentLayoutContext | null>(null);

export function usePaymentLayoutContext() {
  const context = useContext(paymentLayoutContext);

  if (!context) {
    throw new Error(
      'usePaymentLayoutContext must be used within a PaymentLayoutProvider',
    );
  }

  return context;
}

interface PaymentLayoutProviderProps {
  children: ReactNode;
}

export function PaymentLayoutProvider({
  children,
}: PaymentLayoutProviderProps) {
  const [mobileBodyPart, setMobileBodyPart] =
    useState<MobilePaymentPageBodyPart>('add-parties');

  const isMobile = useMediaQuery<AppTheme>((theme) => {
    const mobileMaxSize = theme.breakpoints.values.sm + 1;
    return theme.breakpoints.down(mobileMaxSize);
  });

  const updateMobileBodyPart = useCallback(
    (part: MobilePaymentPageBodyPart) => {
      setMobileBodyPart(part);
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      mobileBodyPart,
      updateMobileBodyPart,
      isMobile,
    }),
    [isMobile, mobileBodyPart, updateMobileBodyPart],
  );

  return (
    <paymentLayoutContext.Provider value={contextValue}>
      {children}
    </paymentLayoutContext.Provider>
  );
}
